// Answer normalization shared by server + client (ka / en / ru).

const MTAVRULI_START = 0x1c90;
const MTAVRULI_END = 0x1cbf;
const MKHEDRULI_OFFSET = 0x1c90 - 0x10d0;

/** Map Georgian Mtavruli capitals to Mkhedruli so "ᲒᲘᲝ" == "გიო". */
export function foldGeorgian(s: string): string {
  let out = "";
  for (const ch of s) {
    const c = ch.codePointAt(0)!;
    out += c >= MTAVRULI_START && c <= MTAVRULI_END ? String.fromCodePoint(c - MKHEDRULI_OFFSET) : ch;
  }
  return out;
}

const EN_ARTICLES = /^(the|a|an)\s+/;

/** Normalize a free-text answer for equality checks. */
export function normalizeAnswer(raw: string): string {
  let s = foldGeorgian(String(raw ?? ""));
  s = s.normalize("NFKD").replace(/[̀-ͯ]/g, ""); // strip Latin diacritics
  s = s.toLowerCase().replace(/ё/g, "е");
  s = s.replace(/[“”„«»"'`´’‘.,!?;:()[\]{}<>…–—\-_/\\|*~^#@%&+=]/g, " ");
  s = s.replace(/\s+/g, " ").trim();
  s = s.replace(EN_ARTICLES, "");
  return s;
}

/** Levenshtein distance (iterative, O(n*m) with two rows). */
export function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  const A = Array.from(a);
  const B = Array.from(b);
  if (!A.length) return B.length;
  if (!B.length) return A.length;
  let prev = new Array(B.length + 1);
  let cur = new Array(B.length + 1);
  for (let j = 0; j <= B.length; j++) prev[j] = j;
  for (let i = 1; i <= A.length; i++) {
    cur[0] = i;
    for (let j = 1; j <= B.length; j++) {
      const cost = A[i - 1] === B[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
    }
    [prev, cur] = [cur, prev];
  }
  return prev[B.length];
}

/** 0..1 similarity on normalized strings. */
export function similarity(a: string, b: string): number {
  const x = normalizeAnswer(a);
  const y = normalizeAnswer(b);
  if (!x || !y) return 0;
  const d = levenshtein(x, y);
  return 1 - d / Math.max(Array.from(x).length, Array.from(y).length);
}

/** True when `guess` should be treated as the real answer (or a near-typo of it). */
export function matchesTruth(guess: string, truth: string, alts: readonly string[] = []): boolean {
  const g = normalizeAnswer(guess);
  if (!g) return false;
  for (const t of [truth, ...alts]) {
    const n = normalizeAnswer(t);
    if (!n) continue;
    if (g === n) return true;
    // digits-only answers must match exactly ("1932" vs "1923" are different lies)
    if (/^\d+$/.test(g) || /^\d+$/.test(n)) continue;
    const len = Math.max(Array.from(g).length, Array.from(n).length);
    if (len >= 5 && similarity(g, n) >= 0.86) return true;
  }
  return false;
}

// Control chars, zero-width chars, bidi overrides (built from escapes to keep the source ASCII).
const INVISIBLE_RE = new RegExp("[" + ["\\u0000-\\u001f", "\\u007f", "\\u200b-\\u200f", "\\u2028-\\u202e", "\\u2066-\\u2069"].join("") + "]", "g");

/** Clean user-entered display text (names, lies). */
export function cleanText(raw: string, max: number): string {
  return String(raw ?? "")
    .replace(INVISIBLE_RE, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max);
}

/** Capitalize the first letter — except Georgian, which has no sentence case
 *  (toUpperCase would turn Mkhedruli into Mtavruli). */
export function capitalizeFirst(s: string): string {
  if (!s) return s;
  const [first, ...rest] = Array.from(s);
  const c = first.codePointAt(0)!;
  const georgian = (c >= 0x10a0 && c <= 0x10ff) || (c >= 0x1c90 && c <= 0x1cbf) || (c >= 0x2d00 && c <= 0x2d2f);
  return georgian ? s : first.toLocaleUpperCase() + rest.join("");
}
